import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AthleteSidebar from "./AthleteSidebar";
import CollegeMap from "./CollegeMap";
import { isAthlete } from "../utils/auth";
import { buildApiUrl } from "../utils/apiConfig";
import "../styles/college-details.css";
import { apiClient } from "../utils/apiClient";

interface CollegeDetails {
    id: number;
    name: string;
    city: string | null;
    state: string | null;
    zip: string | null;
    schoolUrl: string | null;
    locale: string | null;
    size: number | null;
    alias: string | null;
    carnegieBasic: number | null;
    carnegieSizeSetting: number | null;
    carnegieUndergrad: number | null;
    religiousAffiliation: number | null;
    ownership: number | null;
    predominantDegree: number | null;
    highestDegree: number | null;
    admissionRate: number | null;
    satScoreAverage: number | null;
    satScoresMidpoint25thPercentile: number | null;
    satScoresMidpoint75thPercentile: number | null;
    avgCostAttendanceAcademicYear: number | null;
    avgCostConsumerMedianByPredDegree: number | null;
    tuitionInState: number | null;
    tuitionOutOfState: number | null;
    // Median cost by income level (consumer median)
    avgCostConsumerMedianByIncomeLevel0to30000: number | null;
    avgCostConsumerMedianByIncomeLevel30001to48000: number | null;
    avgCostConsumerMedianByIncomeLevel48001to75000: number | null;
    avgCostConsumerMedianByIncomeLevel750001to111000: number | null;
    avgCostConsumerMedianByIncomeLevel110001Plus: number | null;
    percentWhite: number | null;
    percentBlack: number | null;
    percentHispanic: number | null;
    percentAsian: number | null;
    percentAmericanIndianAlaskaNative: number | null;
    percentNativeHawaiianPacificIslander: number | null;
    percentTwoOrMore: number | null;
    percentNonResidentAlien: number | null;
    completionRate: number | null;
    retentionRate: number | null;
    medianEarnings10YrsAfterEntry: number | null;
    medianDebt: number | null;
    cip01Title: string | null;
    cip02Title: string | null;
    cip03Title: string | null;
    cip04Title: string | null;
}

interface CoachData {
    id?: string;
    firstName?: string;
    lastName?: string;
}

export default function CollegeDetailsPage() {
    const { collegeId, athleteId } = useParams<{ collegeId: string; athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [college, setCollege] = useState<CollegeDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [coachData, setCoachData] = useState<CoachData | null>(null);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [activeTab, setActiveTab] = useState<'type' | 'all'>('type');

    // Check if current user is admin/staff viewing an athlete profile
    const isAdminViewingAthlete = !isAthlete() && athleteId;

    // Get team member info from navigation state
    const navState = location.state as { teamMemberId?: string; teamMemberName?: string } | null;

    useEffect(() => {
        const fetchCollegeDetails = async () => {
            if (!collegeId) {
                setError("No college ID provided");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Get team member info from navigation state if available
                if (navState?.teamMemberName) {
                    const nameParts = navState.teamMemberName.split(' ');
                    setCoachData({
                        id: navState.teamMemberId,
                        firstName: nameParts[0] || navState.teamMemberName,
                        lastName: nameParts.slice(1).join(' ') || '',
                    });
                }

                const response = await apiClient(
                    buildApiUrl(`/api/colleges/college/${collegeId}`),
                    {
                        headers: { Accept: "application/json" },
                        credentials: "include" // Use HTTP-only cookie for auth
                    }
                );

                if (response.status === 404) {
                    setError("College not found");
                    setLoading(false);
                    return;
                }

                if (!response.ok) {
                    throw new Error(`Failed to fetch college details: ${response.status}`);
                }

                const data: CollegeDetails = await response.json();
                setCollege(data);
            } catch (error) {
                console.error("Failed to fetch college details:", error);
                setError("Failed to load college details");
            } finally {
                setLoading(false);
            }
        };

        fetchCollegeDetails();
    }, [collegeId, location.state]);

    const formatCurrency = (value: number | null): string => {
        // Treat null, undefined, or NaN as not available
        // Using loose equality to also catch undefined at runtime
        if (value == null || Number.isNaN(value as unknown as number)) return "N/A";
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value as number);
    };

    const formatPercentage = (value: number | null): string => {
        if (value === null) return "N/A";
        return `${(value * 100).toFixed(0)}%`;
    };

    const getOwnershipLabel = (ownership: number | null): string => {
        switch (ownership) {
            case 1:
                return 'Public';
            case 2:
                return 'Private Nonprofit';
            case 3:
                return 'Private For-Profit';
            default:
                return 'Unknown';
        }
    };

    const getLocaleLabel = (locale: string | null): string => {
        if (!locale) return 'Unknown';
        // Simplified locale mapping
        if (locale.includes('11') || locale.includes('12') || locale.includes('13')) return 'City';
        if (locale.includes('21') || locale.includes('22') || locale.includes('23')) return 'Suburb';
        if (locale.includes('31') || locale.includes('32') || locale.includes('33')) return 'Town';
        if (locale.includes('41') || locale.includes('42') || locale.includes('43')) return 'Rural';
        return 'Town';
    };

    const getSizeLabel = (size: number | null): string => {
        if (!size) return 'Unknown';
        if (size < 2000) return 'Small';
        if (size < 15000) return 'Medium';
        return 'Large';
    };

    const toggleSection = (sectionName: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionName)) {
            newExpanded.delete(sectionName);
        } else {
            newExpanded.add(sectionName);
        }
        setExpandedSections(newExpanded);
    };

    const expandAll = () => {
        setExpandedSections(new Set(['fields', 'costs', 'graduation', 'financial', 'earnings', 'diversity', 'test']));
    };

    const closeAll = () => {
        setExpandedSections(new Set());
    };

    const handleNavigateToCollegePlanning = () => {
        if (athleteId) {
            navigate(`/athlete/college-planning/${athleteId}`, {
                state: navState
            });
        } else {
            navigate('/athlete/college-planning');
        }
    };

    if (loading) {
        return (
            <div className="college-details-wrapper">
                <div className="college-details-header-top">
                    <h1 className="college-details-title">College Details</h1>
                </div>
                <div className="college-details-layout">
                    {isAdminViewingAthlete && (
                        <div className="athlete-secondary-sidebar">
                            <AthleteSidebar />
                        </div>
                    )}
                    <div className="college-details-container">
                        <div className="loading-spinner">Loading...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !college) {
        return (
            <div className="college-details-wrapper">
                <div className="college-details-header-top">
                    <h1 className="college-details-title">College Details</h1>
                </div>
                <div className="college-details-layout">
                    {isAdminViewingAthlete && (
                        <div className="athlete-secondary-sidebar">
                            <AthleteSidebar />
                        </div>
                    )}
                    <div className="college-details-container">
                        <div className="error-message">{error || "College not found"}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="college-details-wrapper">
            {/* Main Heading */}
            <div className="college-details-header-top">
                <div className="college-breadcrumb-subtitle">
                    College planning / {college.name}
                </div>
            </div>

            <div className="college-details-layout">
                {/* Show athlete sidebar as secondary sidebar for admin/staff users */}
                {isAdminViewingAthlete && (
                    <div className="athlete-secondary-sidebar">
                        <AthleteSidebar />
                    </div>
                )}

                <div className={`college-details-container ${isAdminViewingAthlete ? 'with-athlete-sidebar' : ''}`}>
                    {/* College Header with Map */}
                    <div className="college-header-card">
                        <div className="college-header-content">
                            <h2 className="college-name-title">{college.name}</h2>
                            <div className="college-student-count">
                                {college.size ? `${college.size.toLocaleString()} undergraduate students` : 'N/A'}
                            </div>
                            {college.schoolUrl && (
                                <a
                                    href={college.schoolUrl.startsWith('http') ? college.schoolUrl : `https://${college.schoolUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="college-website-link-styled"
                                >
                                    {college.schoolUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                </a>
                            )}

                            <div className="college-characteristics">
                                <div className="college-char-item">
                                    <div className="char-icon">📅</div>
                                    <div className="char-label">4-year</div>
                                </div>
                                <div className="college-char-item">
                                    <div className="char-icon">🏛️</div>
                                    <div className="char-label">{getOwnershipLabel(college.ownership)}</div>
                                </div>
                                <div className="college-char-item">
                                    <div className="char-icon">🏘️</div>
                                    <div className="char-label">{getLocaleLabel(college.locale)}</div>
                                </div>
                                <div className="college-char-item">
                                    <div className="char-icon">👥</div>
                                    <div className="char-label">{getSizeLabel(college.size)}</div>
                                </div>
                            </div>
                        </div>

                        <CollegeMap
                            collegeName={college.name}
                            city={college.city}
                            state={college.state}
                            zip={college.zip}
                        />
                    </div>

                    {/* Stats Tabs */}
                    <div className="stats-tabs">
                        <button
                            className={`tab-button ${activeTab === 'type' ? 'active' : ''}`}
                            onClick={() => setActiveTab('type')}
                        >
                            Midpoint for College Type
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
                            onClick={() => setActiveTab('all')}
                        >
                            Midpoint for All Colleges
                        </button>
                    </div>

                    {/* Key Statistics Cards */}
                    <div className="key-stats-grid">
                        {/* Graduation Rate - Vertical Bar */}
                        <div className="key-stat-card">
                            <div className="key-stat-label">Graduation Rate</div>
                            <div className="key-stat-value">{formatPercentage(college.completionRate)}</div>
                            <div className="key-stat-comparison">
                                <div className="comparison-label">Midpoint for 4-yr Colleges: 58%</div>
                                <div className="comparison-bar">
                                    <div
                                        className="comparison-bar-fill"
                                        style={{ height: college.completionRate ? `${college.completionRate * 100}%` : '0%' }}
                                    ></div>
                                    <div className="comparison-marker" style={{ bottom: '58%' }}></div>
                                </div>
                            </div>
                        </div>

                        {/* Average Annual Cost - Horizontal Bar */}
                        <div className="key-stat-card">
                            <div className="key-stat-label">Average Annual Cost</div>
                            <div className="key-stat-value">{formatCurrency(college.avgCostAttendanceAcademicYear)}</div>
                            <div className="key-stat-comparison">
                                <div className="comparison-label">Midpoint for 4-yr Colleges: {formatCurrency(college.avgCostConsumerMedianByPredDegree)}</div>
                                <div className="comparison-bar">
                                    <div
                                        className="comparison-bar-fill"
                                        style={{ width: college.avgCostAttendanceAcademicYear ? `${Math.min((college.avgCostAttendanceAcademicYear / 100000) * 100, 100)}%` : '0%' }}
                                    ></div>
                                    <div className="comparison-marker" style={{ left: `${Math.min((college.avgCostConsumerMedianByPredDegree || 0) / 100000 * 100, 100)}%` }}></div>
                                </div>
                            </div>
                        </div>

                        {/* Median Earnings - Horizontal Bar */}
                        <div className="key-stat-card">
                            <div className="key-stat-label">Median Earnings</div>
                            <div className="key-stat-value">{formatCurrency(college.medianEarnings10YrsAfterEntry)}</div>
                            <div className="key-stat-comparison">
                                <div className="comparison-label">Midpoint for 4-yr Colleges: $53,727</div>
                                <div className="comparison-bar">
                                    <div
                                        className="comparison-bar-fill"
                                        style={{ width: college.medianEarnings10YrsAfterEntry ? `${Math.min((college.medianEarnings10YrsAfterEntry / 100000) * 100, 100)}%` : '0%' }}
                                    ></div>
                                    <div className="comparison-marker" style={{ left: '53.727%' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Expand/Close All Buttons */}
                    <div className="section-controls">
                        <button className="expand-all-btn" onClick={expandAll}>EXPAND ALL</button>
                        <button className="close-all-btn" onClick={closeAll}>CLOSE ALL</button>
                    </div>

                    {/* Accordion Sections */}
                    <div className="accordion-sections">
                        {/* Fields of Study */}
                        {(college.cip01Title || college.cip02Title || college.cip03Title || college.cip04Title) && (
                            <div className="accordion-item">
                                <button
                                    className="accordion-header"
                                    onClick={() => toggleSection('fields')}
                                >
                                    <span className="accordion-title">Fields of Study <span className="badge-count">9</span></span>
                                    <span className="accordion-icon">{expandedSections.has('fields') ? '▲' : '▼'}</span>
                                </button>
                                {expandedSections.has('fields') && (
                                    <div className="accordion-content">
                                        <ul className="programs-list">
                                            {college.cip01Title && <li>• {college.cip01Title}</li>}
                                            {college.cip02Title && <li>• {college.cip02Title}</li>}
                                            {college.cip03Title && <li>• {college.cip03Title}</li>}
                                            {college.cip04Title && <li>• {college.cip04Title}</li>}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Costs */}
                        <div className="accordion-item">
                            <button
                                className="accordion-header"
                                onClick={() => toggleSection('costs')}
                                data-testid="costs-accordion-header"
                            >
                                <span className="accordion-title">Costs</span>
                                <span className="accordion-icon">{expandedSections.has('costs') ? '▲' : '▼'}</span>
                            </button>
                            {expandedSections.has('costs') && (
                                <div className="accordion-content costs-accordion-content">
                                    <div className="costs-layout">
                                        {/* Left Column - Average Annual Cost */}
                                        <div className="costs-left-column">
                                            <div className="average-cost-card">
                                                <div className="average-cost-header">
                                                    <h3 className="average-cost-title">Average Annual Cost</h3>
                                                    <span className="info-icon">ⓘ</span>
                                                </div>
                                                <div className="average-cost-value" data-testid="annual-cost-value">
                                                    {formatCurrency(college.avgCostAttendanceAcademicYear)}
                                                </div>
                                                <p className="average-cost-description">
                                                    Cost includes tuition, living costs, books and supplies, and fees minus the average grants and scholarships for federal financial aid recipients.
                                                </p>

                                                <div className="cost-visualization">
                                                    <div className="cost-tabs">
                                                        <button className="cost-tab active">4-yr Colleges</button>
                                                        <button className="cost-tab">All Colleges</button>
                                                    </div>
                                                    <div className="cost-bar-chart">
                                                        <div className="cost-bar-labels">
                                                            <span className="cost-bar-min">$0</span>
                                                            <span className="cost-bar-max">$100,000</span>
                                                        </div>
                                                        <div className="cost-bar-wrapper">
                                                            <div className="cost-bar-container">
                                                                <div
                                                                    className="cost-bar-fill"
                                                                    style={{ width: `${Math.min((college.avgCostAttendanceAcademicYear || 0) / 100000 * 100, 100)}%` }}
                                                                >
                                                                    <span className="cost-bar-value">{formatCurrency(college.avgCostAttendanceAcademicYear)}</span>
                                                                </div>
                                                            </div>
                                                            <div className="cost-midpoint-marker" style={{ left: `${Math.min((college.avgCostConsumerMedianByPredDegree || 0) / 100000 * 100, 100)}%` }}>
                                                                <div className="midpoint-line"></div>
                                                                <span className="midpoint-label">Midpoint: {formatCurrency(college.avgCostConsumerMedianByPredDegree)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Column - By Family Income */}
                                        <div className="costs-right-column">
                                            <div className="family-income-card">
                                                <h3 className="family-income-title">By Family Income</h3>
                                                <p className="family-income-description">
                                                    Depending on the federal, state, or institutional grant aid available, students in your income bracket may pay more or less than the overall average costs.
                                                </p>

                                                <div className="income-table">
                                                    <div className="income-table-header">
                                                        <div className="income-table-col">Family Income</div>
                                                        <div className="income-table-col">Average Annual Cost</div>
                                                    </div>
                                                    <div className="income-table-body">
                                                        <div className="income-table-row" data-testid="income-0-30k-item">
                                                            <div className="income-table-cell" data-testid="income-0-30k-label">$0-$30,000</div>
                                                            <div className="income-table-cell income-value" data-testid="income-0-30k-value">{formatCurrency(college.avgCostConsumerMedianByIncomeLevel0to30000)}</div>
                                                        </div>
                                                        <div className="income-table-row" data-testid="income-30k-48k-item">
                                                            <div className="income-table-cell" data-testid="income-30k-48k-label">$30,001-$48,000</div>
                                                            <div className="income-table-cell income-value" data-testid="income-30k-48k-value">{formatCurrency(college.avgCostConsumerMedianByIncomeLevel30001to48000)}</div>
                                                        </div>
                                                        <div className="income-table-row" data-testid="income-48k-75k-item">
                                                            <div className="income-table-cell" data-testid="income-48k-75k-label">$48,001-$75,000</div>
                                                            <div className="income-table-cell income-value" data-testid="income-48k-75k-value">{formatCurrency(college.avgCostConsumerMedianByIncomeLevel48001to75000)}</div>
                                                        </div>
                                                        <div className="income-table-row" data-testid="income-75k-111k-item">
                                                            <div className="income-table-cell" data-testid="income-75k-111k-label">$75,001-$110,000</div>
                                                            <div className="income-table-cell income-value" data-testid="income-75k-111k-value">{formatCurrency(college.avgCostConsumerMedianByIncomeLevel750001to111000)}</div>
                                                        </div>
                                                        <div className="income-table-row" data-testid="income-110k-plus-item">
                                                            <div className="income-table-cell" data-testid="income-110k-plus-label">$110,001+</div>
                                                            <div className="income-table-cell income-value" data-testid="income-110k-plus-value">{formatCurrency(college.avgCostConsumerMedianByIncomeLevel110001Plus)}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <a href="https://collegescorecard.ed.gov/netpricecalculator" target="_blank" rel="noopener noreferrer" className="calculate-price-btn">
                                                    <span className="calculator-icon">🖩</span>
                                                    Calculate your personal net price
                                                    <span className="external-link-icon">↗</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Graduation & Retention */}
                        <div className="accordion-item">
                            <button
                                className="accordion-header"
                                onClick={() => toggleSection('graduation')}
                            >
                                <span className="accordion-title">Graduation & Retention</span>
                                <span className="accordion-icon">{expandedSections.has('graduation') ? '▲' : '▼'}</span>
                            </button>
                            {expandedSections.has('graduation') && (
                                <div className="accordion-content">
                                    <div className="graduation-retention-section">
                                        {/* Graduation Rate Section */}
                                        <div className="grad-retention-grid">
                                            <div className="grad-rate-section">
                                                <div className="grad-section-header">
                                                    <h3 className="grad-section-title">Graduation Rate</h3>
                                                    <span className="info-icon">ⓘ</span>
                                                </div>
                                                <div className="pell-grant-checkbox">
                                                    <input type="checkbox" id="pell-grant" />
                                                    <label htmlFor="pell-grant">Show Pell Grant Recipients Only</label>
                                                </div>
                                                <div className="grad-midpoint-label">Show Midpoint For:</div>
                                                <div className="grad-tabs">
                                                    <button className="grad-tab active">4-yr Colleges</button>
                                                    <button className="grad-tab">All Colleges</button>
                                                </div>
                                                <div className="grad-rate-bar-container">
                                                    <div className="grad-rate-labels">
                                                        <span>0%</span>
                                                        <span>100%</span>
                                                    </div>
                                                    <div className="grad-rate-bar">
                                                        <div
                                                            className="grad-rate-bar-fill"
                                                            style={{ width: college.completionRate ? `${college.completionRate * 100}%` : '0%' }}
                                                        >
                                                            <span className="grad-rate-percentage">{formatPercentage(college.completionRate)}</span>
                                                        </div>
                                                        <div className="grad-midpoint-marker" style={{ left: '58%' }}>
                                                            <div className="midpoint-line"></div>
                                                        </div>
                                                    </div>
                                                    <div className="grad-midpoint-text">Midpoint: 58%</div>
                                                </div>
                                            </div>

                                            {/* Students Who Return Section */}
                                            <div className="retention-section">
                                                <div className="grad-section-header">
                                                    <h3 className="grad-section-title">Students Who Return After Their First Year</h3>
                                                    <span className="info-icon">ⓘ</span>
                                                </div>
                                                <div className="retention-donut-container">
                                                    <svg className="retention-donut" viewBox="0 0 200 200">
                                                        <circle
                                                            cx="100"
                                                            cy="100"
                                                            r="70"
                                                            fill="none"
                                                            stroke="#e5e7eb"
                                                            strokeWidth="30"
                                                        />
                                                        <circle
                                                            cx="100"
                                                            cy="100"
                                                            r="70"
                                                            fill="none"
                                                            stroke="#2563eb"
                                                            strokeWidth="30"
                                                            strokeDasharray={`${(college.retentionRate || 0) * 439.8} 439.8`}
                                                            transform="rotate(-90 100 100)"
                                                        />
                                                        <text
                                                            x="100"
                                                            y="110"
                                                            textAnchor="middle"
                                                            fontSize="36"
                                                            fontWeight="700"
                                                            fill="#1a1a1a"
                                                        >
                                                            {formatPercentage(college.retentionRate)}
                                                        </text>
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Outcomes 8 Years After Attending */}
                                        <div className="outcomes-section">
                                            <div className="grad-section-header">
                                                <h3 className="grad-section-title">Outcomes 8 Years After Attending</h3>
                                                <span className="info-icon">ⓘ</span>
                                            </div>
                                            <div className="pell-grant-checkbox">
                                                <input type="checkbox" id="pell-grant-outcomes" />
                                                <label htmlFor="pell-grant-outcomes">Show Pell Grant Recipients Only</label>
                                            </div>
                                            <div className="outcomes-filters">
                                                <div className="outcomes-filter-group">
                                                    <span className="filter-label">Show data for students who</span>
                                                    <button className="filter-btn">started college here</button>
                                                    <button className="filter-btn">transferred in</button>
                                                    <button className="filter-btn active">both</button>
                                                </div>
                                                <div className="outcomes-filter-group">
                                                    <span className="filter-label">and started their studies</span>
                                                    <button className="filter-btn">full-time</button>
                                                    <button className="filter-btn">part-time</button>
                                                    <button className="filter-btn active">both</button>
                                                </div>
                                            </div>
                                            <div className="outcomes-student-count">
                                                Out of {college.size ? Math.floor(college.size * 0.1).toLocaleString() : '1,391'} students...
                                            </div>
                                            <div className="outcomes-bars">
                                                <div className="outcome-item">
                                                    <div className="outcome-label">Graduated</div>
                                                    <div className="outcome-bar-wrapper">
                                                        <div className="outcome-bar">
                                                            <div
                                                                className="outcome-bar-fill graduated"
                                                                style={{ width: college.completionRate ? `${college.completionRate * 100}%` : '0%' }}
                                                            >
                                                                <span className="outcome-percentage">{formatPercentage(college.completionRate)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="outcome-item">
                                                    <div className="outcome-label">Transferred</div>
                                                    <div className="outcome-bar-wrapper">
                                                        <div className="outcome-bar">
                                                            <div className="outcome-bar-fill transferred" style={{ width: '6%' }}>
                                                                <span className="outcome-percentage">6%</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="outcome-item">
                                                    <div className="outcome-label">Withdrew</div>
                                                    <div className="outcome-bar-wrapper">
                                                        <div className="outcome-bar">
                                                            <div className="outcome-bar-fill withdrew" style={{ width: '2%' }}>
                                                                <span className="outcome-percentage">2%</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Financial Aid & Debt */}
                        <div className="accordion-item">
                            <button
                                className="accordion-header"
                                onClick={() => toggleSection('financial')}
                            >
                                <span className="accordion-title">Financial Aid & Debt</span>
                                <span className="accordion-icon">{expandedSections.has('financial') ? '▲' : '▼'}</span>
                            </button>
                            {expandedSections.has('financial') && (
                                <div className="accordion-content">
                                    <div className="detail-stat-grid">
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">Median Debt</div>
                                            <div className="detail-stat-value">{formatCurrency(college.medianDebt)}</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Typical Earnings */}
                        <div className="accordion-item">
                            <button
                                className="accordion-header"
                                onClick={() => toggleSection('earnings')}
                            >
                                <span className="accordion-title">Typical Earnings</span>
                                <span className="accordion-icon">{expandedSections.has('earnings') ? '▲' : '▼'}</span>
                            </button>
                            {expandedSections.has('earnings') && (
                                <div className="accordion-content">
                                    <div className="detail-stat-grid">
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">Median Earnings (10 years after entry)</div>
                                            <div className="detail-stat-value">{formatCurrency(college.medianEarnings10YrsAfterEntry)}</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Campus Diversity */}
                        <div className="accordion-item">
                            <button
                                className="accordion-header"
                                onClick={() => toggleSection('diversity')}
                            >
                                <span className="accordion-title">Campus Diversity</span>
                                <span className="accordion-icon">{expandedSections.has('diversity') ? '▲' : '▼'}</span>
                            </button>
                            {expandedSections.has('diversity') && (
                                <div className="accordion-content">
                                    <div className="detail-stat-grid">
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">White</div>
                                            <div className="detail-stat-value">{formatPercentage(college.percentWhite)}</div>
                                        </div>
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">Black</div>
                                            <div className="detail-stat-value">{formatPercentage(college.percentBlack)}</div>
                                        </div>
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">Hispanic</div>
                                            <div className="detail-stat-value">{formatPercentage(college.percentHispanic)}</div>
                                        </div>
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">Asian</div>
                                            <div className="detail-stat-value">{formatPercentage(college.percentAsian)}</div>
                                        </div>
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">American Indian/Alaska Native</div>
                                            <div className="detail-stat-value">{formatPercentage(college.percentAmericanIndianAlaskaNative)}</div>
                                        </div>
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">Native Hawaiian/Pacific Islander</div>
                                            <div className="detail-stat-value">{formatPercentage(college.percentNativeHawaiianPacificIslander)}</div>
                                        </div>
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">Two or More Races</div>
                                            <div className="detail-stat-value">{formatPercentage(college.percentTwoOrMore)}</div>
                                        </div>
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">Non-Resident Alien</div>
                                            <div className="detail-stat-value">{formatPercentage(college.percentNonResidentAlien)}</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Test Scores and Acceptance */}
                        <div className="accordion-item">
                            <button
                                className="accordion-header"
                                onClick={() => toggleSection('test')}
                            >
                                <span className="accordion-title">Test Scores and Acceptance</span>
                                <span className="accordion-icon">{expandedSections.has('test') ? '▲' : '▼'}</span>
                            </button>
                            {expandedSections.has('test') && (
                                <div className="accordion-content">
                                    <div className="detail-stat-grid">
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">Acceptance Rate</div>
                                            <div className="detail-stat-value">{formatPercentage(college.admissionRate)}</div>
                                        </div>
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">Average SAT</div>
                                            <div className="detail-stat-value">{college.satScoreAverage || 'N/A'}</div>
                                        </div>
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">SAT 25th Percentile</div>
                                            <div className="detail-stat-value">{college.satScoresMidpoint25thPercentile || 'N/A'}</div>
                                        </div>
                                        <div className="detail-stat-item">
                                            <div className="detail-stat-label">SAT 75th Percentile</div>
                                            <div className="detail-stat-value">{college.satScoresMidpoint75thPercentile || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
